import { normalizeServiceCatalog, contractorServiceLabel, type ContractorServiceOffering } from './contractorServiceCatalog';
/**
 * Hotshot hauler rate card — a company's own pricing, mirroring the WM hauler
 * rate card (`waste_hauler_rate_cards.card_json`). Stored as one JSON blob in
 * `hotshot_rate_cards.card_json` so new fields need no schema change.
 *
 * Listed cards are edited from Contractor Book → Add my company and shown on
 * contractor cards. Customer quotes still use platform `HOTSHOT_PRICING` until
 * a listed card is selected at checkout (HotshotHub Phase 4b).
 */
import { OPERATING_SERVICES, normalizeServiceProfiles, type OperatingService, type ServiceRateProfiles } from './serviceOperatingCosts';
import { HOTSHOT_PRICING, hotshotTripUsdWithRates } from './guestQuotePricing';

export type HotshotUrgencyKey = 'standard' | 'rush' | 'critical';

export interface HotshotRateCard {
  serviceProfiles?: ServiceRateProfiles;
  serviceCatalog?: ContractorServiceOffering[];
  /** Per loaded mile by urgency (customer pays round-trip miles). */
  perMile: Record<HotshotUrgencyKey, number>;
  /** Long-haul floor (deadhead) and in-town floor. */
  minTripUsd: number;
  localMinTripUsd: number;
  /** Trailer / heavy adders. */
  gooseneckAdderUsd: number;
  heavyAdderUsd: number;
  /** Show this company + its rate on the marketplace (Phase 4b gate). */
  listOnMarketplace: boolean;
  /** Temporary sale, 0–50%. */
  promoPercentOff: number;
  /** Where the yard is, for miles-from-base (display only in 4a). */
  baseCity: string;
  yardLat: number | null;
  yardLng: number | null;
}

/** Customer-facing name for the pinned platform default (WM analog: Thompson Hauling). */
export const DEFAULT_HOTSHOT_HAULER_NAME = 'Zia Hotshots';

/** The platform defaults — what an operator sees before they set their own. */
export function platformHotshotRateCard(): HotshotRateCard {
  return {
    serviceProfiles: {},
    serviceCatalog: [],
    perMile: { ...HOTSHOT_PRICING.ratePerMileUsd },
    minTripUsd: HOTSHOT_PRICING.minTripUsd,
    localMinTripUsd: HOTSHOT_PRICING.localMinTripUsd,
    gooseneckAdderUsd: HOTSHOT_PRICING.goosenecKAdderUsd,
    heavyAdderUsd: HOTSHOT_PRICING.heavyAdderUsd,
    listOnMarketplace: false,
    promoPercentOff: 0,
    baseCity: '',
    yardLat: null,
    yardLng: null,
  };
}

function money(value: unknown, max: number, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.min(max, Math.round(n * 100) / 100) : fallback;
}

function coord(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Coerce any stored / submitted blob into a safe, fully-populated card. */
export function normalizeHotshotRateCard(raw: unknown): HotshotRateCard {
  const base = platformHotshotRateCard();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Record<string, unknown>;
  const pm = (r.perMile && typeof r.perMile === 'object' ? r.perMile : {}) as Record<string, unknown>;
  return {
    serviceProfiles: normalizeServiceProfiles(r.serviceProfiles),
    serviceCatalog: normalizeServiceCatalog(r.serviceCatalog),
    perMile: {
      standard: money(pm.standard, 50, base.perMile.standard),
      rush: money(pm.rush, 50, base.perMile.rush),
      critical: money(pm.critical, 50, base.perMile.critical),
    },
    minTripUsd: money(r.minTripUsd, 5000, base.minTripUsd),
    localMinTripUsd: money(r.localMinTripUsd, 5000, base.localMinTripUsd),
    gooseneckAdderUsd: money(r.gooseneckAdderUsd, 2000, base.gooseneckAdderUsd),
    heavyAdderUsd: money(r.heavyAdderUsd, 5000, base.heavyAdderUsd),
    listOnMarketplace: r.listOnMarketplace === true,
    promoPercentOff: Math.max(0, Math.min(50, Math.round(Number(r.promoPercentOff) || 0))),
    baseCity: String(r.baseCity || '').slice(0, 80),
    yardLat: coord(r.yardLat),
    yardLng: coord(r.yardLng),
  };
}

/**
 * A display-only preview of the hauler's take on a sample run. Pure mirror of
 * the customer maths (round-trip miles × per-mile, floored, promo) so the
 * operator sees what their card yields WITHOUT this being wired into checkout.
 */
export function previewHotshotTakeUsd(
  card: HotshotRateCard,
  input: { oneWayMiles: number; urgency: HotshotUrgencyKey; local?: boolean; gooseneck?: boolean; heavy?: boolean },
): number {
  // Delegate to the ONE money kernel so the desk preview == the customer charge.
  return hotshotTripUsdWithRates(
    {
      rawPickupToDropMiles: Math.max(0, input.oneWayMiles),
      urgency: input.urgency,
      loadClass: input.gooseneck ? 'gooseneck' : input.heavy ? 'heavy_confirm' : null,
      localRun: Boolean(input.local),
    },
    card,
  );
}

/** Compact line for contractor book / Operator Desk Book · Multiple cards. */
export function contractorRateCardSummary(card: HotshotRateCard | null | undefined): string | null {
  if (!card) return null;
  if (card.serviceCatalog?.length) return card.serviceCatalog.map(s => {
    const priced = s.products.filter(p => p.priceUsd != null);
    const preview = priced.slice(0, 2).map(p => `${p.name}: $${p.priceUsd!.toFixed(2)}/${p.unit}`).join(', ');
    return `${contractorServiceLabel(s.service)}${preview ? ` · ${preview}${priced.length > 2 ? ` · ${priced.length - 2} more prices` : ''}` : s.products.length ? ' · Quote required' : ''}`;
  }).join(' | ');
  const profiles = Object.entries(card.serviceProfiles || {});
  if (profiles.length) return profiles.map(([service, rate]) => `${OPERATING_SERVICES[service as OperatingService]} · ${rate.vehicleBasis === 'hour' ? `$${rate.vehicleHourlyUsd.toFixed(2)}/hr truck` : `$${rate.wearPerMileUsd.toFixed(2)}/mi vehicle`} · $${rate.laborHourlyUsd.toFixed(2)}/hr labor`).join(' | ');
  const parts = [
    `$${card.perMile.standard.toFixed(2)}/mi`,
    `pad $${Math.round(card.minTripUsd)}`,
  ];
  if (card.baseCity.trim()) parts.push(card.baseCity.trim());
  if (card.promoPercentOff > 0) parts.push(`${card.promoPercentOff}% off`);
  return parts.join(' · ');
}
